import Tool from '../Tool';

/** Line tool base class */
export default class LineTool extends Tool {
	/**
	 * Calculate the distance between two points
	 * @param {number[]} coord1 - Coordinates for point 1
	 * @param {number[]} coord2 - Coordinates for point 2
	 * @return {number} Distance between the given points
	 */
	distance(coord1, coord2) {
		let xs = coord2[0] - coord1[0];
		let ys = coord2[1] - coord1[1];

		xs *= xs; // Square the x-distance
		ys *= ys; // Square the y-distance

		return Math.sqrt(xs + ys);
	}

	/**
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {number} style.color - Tool line color
	 * @property {number} style.width - Tool line width
	 */
	_getDefaultParams() {
		return {
			id: 'line',
			name: 'Trendline',
			style: this.core.getStyle('tool.line')
		};
	}

	/**
	 * Determine if current mouse coordinates are within hitbox of this tool
	 * @param {number[]} mousePanel - Array of coordinates for current mouse location
	 * @return {boolean} - Whether or not mouse is within hitbox
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	/**
	 * Determine if current mouse coordinates are within tolerance area for line
	 * @param {number[]} mousePanel - Array of coordinates for current mouse location
	 * @return {boolean} - Whether or not mouse is within tolerance area
	 */
	_isWithinLineTool(mousePanel) {
		const handles = this.handle;

		if (handles.length === 2) {
			// use either regular handle coords or special endpoint coords (if present)
			const coords = [
				this.state.endpointLeft || handles[0].getCoords(),
				this.state.endpointRight || handles[1].getCoords()
			];

			// couldn't find one or more handles
			if (!coords[0] || !coords[1]) {
				return false;
			}

			let tolerance = 5 + this.params.style.width / 2;
			const isVertical = coords[0][0] === coords[1][0];
			const left = Math.min(coords[0][0], coords[1][0]);
			const right = Math.max(coords[0][0], coords[1][0]);
			const top = Math.min(coords[0][1], coords[1][1]);
			const bottom = Math.max(coords[0][1], coords[1][1]);

			// use a larger hitbox for touch events
			if (this.panel.core.state.isTouchDevice) {
				tolerance += 10;
			}

			// handle vertical lines as a simple bounding box intersection check
			if (isVertical) {
				const r1 = {
					x: left - tolerance,
					y: top,
					width: right - left + 2 * tolerance,
					height: bottom - top
				};
				const r2 = {
					x: mousePanel[0],
					y: mousePanel[1],
					width: 1,
					height: 1
				};

				return this.intersect(r1, r2);
			}

			// handle diagonal or horizontal lines with a distance calculation
			const linepoint = this._getClosestPoint(
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1],
				mousePanel[0],
				mousePanel[1]
			);
			const dx = mousePanel[0] - linepoint[0];
			const dy = mousePanel[1] - linepoint[1];
			const distance = Math.abs(Math.sqrt(dx * dx + dy * dy));

			if (
				distance > tolerance ||
				mousePanel[0] < left ||
				mousePanel[0] > right
			) {
				return false;
			} else if (distance < tolerance) {
				return true;
			}
		}
	}

	/**
	 * Called from core.onClick() on both first and second clicks.
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onClickCanvas(mouse) {
		const { core } = this;
		const { state } = core;
		const { isUpper } = this.panel;
		const numberOfAnchors = this.params.numberOfAnchors || 2;

		if (!this.state.inProgressHandle) {
			// first click
			let mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// use snapHandle coords if available
			if (isUpper && state.snapHandle?.coords) {
				mousePanel = [
					state.snapHandle.coords[0],
					state.snapHandle.coords[1] - this.panel.size.top
				];
			}

			// first click - create and render first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle (for its coords) but don't render yet
			this.handle[1] = this.createHandle(mousePanel);

			// Exit out of the handle placement if only one handle is necessary
			if (numberOfAnchors === 1) {
				this.state.inProgressHandle = false;
				this.state.dragHandle = null;
				return;
			}
			this.state.dragHandle = this.handle[1];

			// tool is now active
			this.state.inProgressHandle = true;
		} else {
			// second click

			// Special case for adding extended horizontal line when using a touch device,
			// with a single touch and no drag (CH-1466)
			if (
				state.isTouchDevice &&
				mouse &&
				/^extendedhorizontal/.test(this.params.id)
			) {
				const valIn = this.panel.yAxis.primaryScale.invert(mouse[1]);

				// Always store handle values as actuals (not normalized)
				const value = this.panel.isNormalized
					? this.panel.normalizedToActual(valIn).toFixed(4)
					: valIn.toFixed(4);

				this.handle[0].params.value = Number(value);
				this.handle[1].params.value = Number(value);

			} else if (
				isUpper &&
				state.snapHandle?.coords &&
				!this.params.lockVertical
			) { // use snapHandle coords if available
				const snapDateIdx = Math.round(
					this.panel.xAxis.primaryScale.invert(state.snapHandle.coords[0])
				);

				if (core.exchangeDates[snapDateIdx]) {
					let val = this.panel.yAxis.primaryScale.invert(
						state.snapHandle.coords[1]
					);

					// always store handle values in actuals
					if (this.panel.isNormalized) {
						val = this.panel.normalizedToActual(val);
					}

					this.state.dragHandle.params.value = Number(val);
					this.state.dragHandle.params.date = new Date(core.exchangeDates[snapDateIdx]).getTime();
				}
			}

			// no longer actively drawing a change in the tool
			this.state.inProgressHandle = false;
			this.state.dragHandle = null;

			this.onComplete();
		}
	}

	/**
	 * @param {number[]} mouse - Array of coordinates for current mouse location
	 */
	onMousemove(mouse) {
		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		this.core.onToolMousemove(this, mousePanel);

		if (this.state.inProgressHandle) {
			let idx = Math.round(
				this.panel.xAxis.primaryScale.invert(mousePanel[0])
			);

			idx = Math.min(this.core.exchangeDates.length - 1, idx);
			idx = Math.max(0, idx);

			this.state.dragHandle.params.date = this.core.exchangeDates[idx];

			if (!this.params.lockVertical) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
			}
		} else {
			this.dragTool(mousePanel);
		}
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// couldn't find one or more handles
		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;
		const distX = coords[1][0] - coords[0][0];
		const distY = coords[1][1] - coords[0][1];

		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = '#FFF';
		ctx.lineWidth = this.params.style.width;

		// regular line segment
		ctx.moveTo(coords[0][0], coords[0][1]);

		if (this.params.lockVertical) {
			ctx.lineTo(coords[1][0], coords[0][1]);
		} else {
			ctx.lineTo(coords[1][0], coords[1][1]);
		}
		ctx.stroke();

		// arrowhead
		if (this.params.arrow) {
			this.drawArrowheads(
				ctx,
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1]
			);
		}

		// extend right and store endpoint
		if (this.params.extendRight) {
			this.state.endpointRight = this._renderRay(
				ctx,
				coords[1][0],
				coords[1][1],
				distX,
				distY
			);
		} else {
			this.state.endpointRight = null;
		}

		// extend left and store endpoint
		if (this.params.extendLeft) {
			this.state.endpointLeft = this._renderRay(
				ctx,
				coords[0][0],
				coords[0][1],
				-distX,
				-distY
			);
		} else {
			this.state.endpointLeft = null;
		}

		ctx.stroke();

		// handles
		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw extended line to canvas edge
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} x - X-coordinate for starting point of ray
	 * @param {number} y - Y-coordinate for starting point of ray
	 * @param {number} distX - X-distance to edge of canvas
	 * @param {number} distY - Y-distance to edge of canvas
	 * @return {number[]} - Starting point of ray
	 */
	_renderRay(ctx, x, y, distX, distY) {
		// don't render short rays
		if (Math.abs(distX) < 1 && Math.abs(distY) < 1) {
			return [x, y];
		}

		const size = this.panel.size;
		const right = size.width + size.padding.left;
		const left = size.padding.left;
		const isVertical = distX === 0;

		ctx.moveTo(x, y);

		if (isVertical) {
			if (distY >= 0 && 1 / distY !== -Infinity) {
				// vertical to bottom edge, don't include negative 0

				y = size.padding.top + size.height;
			} else {
				// vertical to top edge

				y = size.padding.top;
			}

			ctx.lineTo(x, y);
		} else {
			if (distX > 0) {
				// horizontal to right edge

				while (x < right) {
					x += distX;
					y += distY;

					ctx.lineTo(x, y);
				}
			} else {
				// horizontal to left edge

				while (x >= left) {
					x += distX;
					y += distY;

					ctx.lineTo(x, y);
				}
			}
		}

		return [x, y];
	}
}
